import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/contexts/AdminContext";
import AppShell from "@/components/AppShell";
import AdminPinCard from "@/components/AdminPinCard";
import {
  Lock, Palette, Sparkles, ChevronRight, Type, Mail,
} from "lucide-react";

/**
 * /admin — Back Office hub for the platform owner.
 *
 * Access is gated by the Back Office PIN. Once unlocked, this hub
 * provides tile-based navigation to Branding, Analytics, and the
 * Daily Digest controls. Sensitive sub-endpoints still verify the
 * admin email server-side as a second factor.
 */
export default function Admin() {
  const { user } = useAuth();
  const { unlocked } = useAdmin();
  const email = (user?.email || "").toLowerCase();

  if (!unlocked) {
    return (
      <AppShell>
        <div className="bd-surface p-6 max-w-lg" data-testid="admin-locked">
          <div className="flex items-center gap-2 font-bold text-lg mb-2">
            <Lock size={16} /> Back Office is locked
          </div>
          <p style={{ color: "var(--text-dim)" }}>
            Tap the <strong>Back Office</strong> pill at the bottom of the screen and enter your passcode to unlock app-wide admin controls.
          </p>
        </div>
      </AppShell>
    );
  }

  const tiles = [
    {
      to: "/admin/branding", label: "Branding · Logo & Fonts",
      desc: "Upload your logo, set the wordmark, edit the front-page slogan, and pick a site-wide font.",
      icon: Type, testId: "admin-tile-branding",
    },
    {
      to: "/admin/analytics", label: "Analytics · Global reach",
      desc: "Signups, profiles created, birthdays added, and where users are around the world.",
      icon: Sparkles, testId: "admin-tile-analytics",
    },
    {
      to: "/admin/digest", label: "Email · Daily Digest",
      desc: "Send today's digest now, or fire a preview to yourself.",
      icon: Mail, testId: "admin-tile-digest",
    },
    {
      to: "/profile", label: "Theme: Colors & Fonts",
      desc: "Switch theme, adjust font, set personal display defaults.",
      icon: Palette, testId: "admin-tile-theme",
    },
  ];

  return (
    <AppShell>
      <div className="flex flex-col gap-6 max-w-5xl">
        <div>
          <div className="text-xs uppercase tracking-widest"
               style={{ color: "var(--text-dim)" }}>
            Back Office
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold"
              style={{ letterSpacing: "-0.04em" }}>
            Run Born Day.
          </h1>
          <p style={{ color: "var(--text-dim)" }} className="mt-2">
            Signed in as <span className="bd-mono">{email}</span>.{" "}
            <span style={{ color: "var(--primary)" }}>Back Office is unlocked.</span>
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-tiles">
          {tiles.map((t) => (
            <Link key={t.to} to={t.to} className="bd-card flex flex-col gap-2 transition hover:scale-[1.01]"
                  style={{ padding: "1rem 1.1rem" }}
                  data-testid={t.testId}>
              <div className="flex items-center gap-2">
                <t.icon size={18} style={{ color: "var(--primary)" }} />
                <span className="font-bold">{t.label}</span>
              </div>
              <p className="text-xs" style={{ color: "var(--text-dim)" }}>{t.desc}</p>
              <div className="text-[11px] uppercase tracking-wider flex items-center gap-1 mt-1"
                   style={{ color: "var(--text-dim)" }}>
                Open <ChevronRight size={12} />
              </div>
            </Link>
          ))}
        </div>

        {/* Passcode rotation — kept below the tile grid so it doesn't
            distract from the top-level admin nav, but pinned on this
            page (not a separate route) so a first-time admin sees the
            "still on factory default" warning immediately. */}
        <div className="max-w-2xl">
          <AdminPinCard />
        </div>
      </div>
    </AppShell>
  );
}
